import { UseFormReturn } from 'react-hook-form';
import { MiscellaneousField } from '../../reportsTypes';
import { useState } from 'react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  SelectType,
  Button,
  ScrollArea,
} from '@ais-ui/ui-core';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';

interface MultiSelectDualPanelItemProps {
  fieldName: string;
  form: UseFormReturn;
  fieldInfo: MiscellaneousField;
}

export const MiscellaneousMultiSelectDualPanelItem = ({
  fieldName,
  form,
  fieldInfo,
}: MultiSelectDualPanelItemProps) => {
  const [selectables] = useState<SelectType[]>(
    fieldInfo.options as SelectType[]
  );
  const [hoveredAvailable, setHoveredAvailable] = useState<string | null>(null);
  const { messages } = useLocalization();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        // Convert between SelectType[] (form data) and string[] (internal state)
        type SelectedField = SelectType | string;
        const selectedFields = (field.value as SelectedField[]) || [];
        const selectedValues = selectedFields.map((item) =>
          typeof item === 'string' ? item : item.value
        );
        const sanitizedFieldId = fieldName.replace(/[^a-zA-Z0-9_-]/g, '-');

        const unselectedFields = selectables.filter(
          (selectable) => !selectedValues.includes(selectable.value)
        );

        const handleAddField = (value: string) => {
          const selectableItem = selectables.find((s) => s.value === value);
          if (selectableItem) {
            const newSelected = [...selectedFields, selectableItem];
            field.onChange(newSelected);
            form.clearErrors();
          }
        };

        const handleRemoveField = (value: string) => {
          const newSelected = selectedFields.filter((item) => {
            const itemValue = typeof item === 'string' ? item : item.value;
            return itemValue !== value;
          });
          field.onChange(newSelected);
        };

        const getFieldLabel = (value: string) => {
          const selectable = selectables.find((s) => s.value === value);
          return selectable ? selectable.label : value;
        };

        return (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {fieldInfo.name}: {fieldInfo.required && '*'}
              </FormLabel>
              {fieldInfo.help && (
                <InfoIconTooltip className="mr-2">
                  {fieldInfo.help}
                </InfoIconTooltip>
              )}
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Available Fields Panel */}
                <div className="border rounded-lg p-3 h-fit">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">
                        Available Options
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {unselectedFields.length} option
                        {unselectedFields.length !== 1 ? 's' : ''} available
                      </p>
                    </div>
                    {unselectedFields.length > 0 && (
                      <Button
                        id={`${sanitizedFieldId}-add-all-button`}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allSelectableItems = unselectedFields.map(
                            (f) => f
                          );
                          field.onChange([
                            ...selectedFields,
                            ...allSelectableItems,
                          ]);
                        }}
                      >
                        Add all
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-1 pr-4">
                      {unselectedFields.length === 0 ? (
                        <div className="flex items-center justify-center h-[148px] text-sm text-muted-foreground">
                          All options selected
                        </div>
                      ) : (
                        unselectedFields.map((selectable) => (
                          <div
                            key={selectable.value}
                            className={cn(
                              'flex items-center justify-between p-1.5 rounded-md transition-colors cursor-pointer',
                              hoveredAvailable === selectable.value
                                ? 'bg-muted'
                                : 'hover:bg-muted/50'
                            )}
                            onMouseEnter={() =>
                              setHoveredAvailable(selectable.value)
                            }
                            onMouseLeave={() => setHoveredAvailable(null)}
                            onClick={() => handleAddField(selectable.value)}
                          >
                            <span className="text-sm flex-1 truncate">
                              {selectable.label}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddField(selectable.value);
                              }}
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Selected Fields Panel */}
                <div className="border rounded-lg p-3 h-fit">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">
                        Selected Options
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {selectedFields.length} selected
                      </p>
                    </div>
                    {selectedFields.length > 0 && (
                      <Button
                        id={`${sanitizedFieldId}-clear-button`}
                        variant="ghost"
                        size="sm"
                        onClick={() => field.onChange([])}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-1.5 pr-4">
                      {selectedFields.length === 0 ? (
                        <div className="flex items-center justify-center h-[148px] text-sm text-muted-foreground">
                          {messages.miscNoOptionsSelected}
                        </div>
                      ) : (
                        selectedFields.map((item) => {
                          const itemValue =
                            typeof item === 'string' ? item : item.value;
                          return (
                            <div
                              key={itemValue}
                              className="flex items-center justify-between p-1.5 bg-muted/30 border rounded-md transition-all hover:bg-muted"
                            >
                              <span
                                className="flex-1 text-sm truncate min-w-0 max-w-[200px]"
                                title={getFieldLabel(itemValue)}
                              >
                                {getFieldLabel(itemValue)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 flex-shrink-0 ml-2"
                                onClick={() => handleRemoveField(itemValue)}
                              >
                                <ChevronLeft className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
